import { evaluateCompletion } from "./completion-validation.js";
export class TwoPointDrawSession {
    #options;
    #status = "ready";
    #start;
    #cursor;
    #completed;
    #rejection;
    constructor(options) {
        if (!options.id.trim()) {
            throw new TypeError("TwoPointDrawSession id must not be empty.");
        }
        if (!options.plotType.trim()) {
            throw new TypeError("TwoPointDrawSession plotType must not be empty.");
        }
        this.#options = options;
    }
    get status() {
        return this.#status;
    }
    snapshot() {
        if (this.#status === "completed" && this.#completed) {
            return { status: this.#status, completed: this.#completed };
        }
        const draft = this.#createDraft();
        return {
            status: this.#status,
            ...(draft ? { draft } : {}),
            ...(this.#rejection ? { rejection: this.#rejection } : {}),
        };
    }
    click(position) {
        if (this.#isTerminal()) {
            return this.snapshot();
        }
        const point = clonePosition(position);
        if (!this.#start) {
            this.#start = point;
            this.#cursor = undefined;
            this.#rejection = undefined;
            this.#status = "drawing";
            return this.snapshot();
        }
        if (samePosition(this.#start, point)) {
            return this.snapshot();
        }
        this.#cursor = point;
        this.#rejection = undefined;
        return this.#tryComplete([this.#start, point]);
    }
    doubleClick(position) {
        return this.click(position);
    }
    pointerMove(position) {
        if (this.#isTerminal() || !this.#start) {
            return this.snapshot();
        }
        const point = clonePosition(position);
        this.#rejection = undefined;
        this.#cursor = samePosition(this.#start, point) ? undefined : point;
        return this.snapshot();
    }
    keyDown(key) {
        if (this.#isTerminal()) {
            return this.snapshot();
        }
        if (key === "Escape") {
            return this.cancel();
        }
        if (key === "Backspace" || key === "Delete") {
            this.#start = undefined;
            this.#cursor = undefined;
            this.#rejection = undefined;
            this.#status = "ready";
            return this.snapshot();
        }
        if (key === "Enter" && this.#start && this.#cursor) {
            this.#rejection = undefined;
            return this.#tryComplete([this.#start, this.#cursor]);
        }
        return this.snapshot();
    }
    cancel() {
        if (this.#status !== "completed") {
            this.#status = "cancelled";
            this.#start = undefined;
            this.#cursor = undefined;
            this.#rejection = undefined;
        }
        return this.snapshot();
    }
    #createDraft() {
        if (!this.#start || !this.#cursor || this.#isTerminal()) {
            return undefined;
        }
        return this.#createFeature([this.#start, this.#cursor]);
    }
    #tryComplete(controlPoints) {
        const candidate = this.#createFeature(controlPoints);
        const evaluation = evaluateCompletion(this.#options.validateCompletion, candidate);
        if (!evaluation.valid) {
            this.#rejection = evaluation.rejection;
            this.#status = "drawing";
            return this.snapshot();
        }
        this.#completed = candidate;
        this.#rejection = undefined;
        this.#status = "completed";
        return this.snapshot();
    }
    #createFeature(controlPoints) {
        const input = {
            id: this.#options.id,
            plotType: this.#options.plotType,
            controlPoints: controlPoints.map(clonePosition),
        };
        return {
            ...input,
            ...(this.#options.definitionVersion !== undefined
                ? { definitionVersion: this.#options.definitionVersion }
                : {}),
            ...(this.#options.parameters !== undefined
                ? { parameters: { ...this.#options.parameters } }
                : {}),
            ...(this.#options.style !== undefined
                ? { style: { ...this.#options.style } }
                : {}),
            ...(this.#options.metadata !== undefined
                ? { metadata: { ...this.#options.metadata } }
                : {}),
        };
    }
    #isTerminal() {
        return this.#status === "completed" || this.#status === "cancelled";
    }
}
function clonePosition([longitude, latitude]) {
    return [longitude, latitude];
}
function samePosition(left, right) {
    return left[0] === right[0] && left[1] === right[1];
}
//# sourceMappingURL=two-point-draw-session.js.map